import React from 'react';
import { Lock, Unlock, Construction, Crown, Gem, Watch, Zap, Sparkles, ArrowUpCircle, Trophy, BookOpen, Clock, Ticket } from 'lucide-react';
import { useSiteLock } from '../../context/SiteLockContext';
import { useLanguage } from '../../context/LanguageContext';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const LOCKABLE_PAGES = [
    { id: 'meta', icon: Crown, label: 'Meta' },
    { id: 'relics', icon: Gem, label: 'Relics' },
    { id: 'accessories', icon: Watch, label: 'Accessories' },
    { id: 'skills', icon: Zap, label: 'Skills' },
    { id: 'gachas', icon: Sparkles, label: 'Gachas' },
    { id: 'upgrades', icon: ArrowUpCircle, label: 'Upgrades' },
    { id: 'achievements', icon: Trophy, label: 'Achievements' },
    { id: 'tutorials', icon: BookOpen, label: 'Tutorials' },
    { id: 'timers', icon: Clock, label: 'Timers' },
    { id: 'codes', icon: Ticket, label: 'Codes' },
];

export const LockModeManager = () => {
    const { togglePageLock, isPageLockedForAdmin } = useSiteLock();
    const { t } = useLanguage();

    const handleToggle = async (pageId) => {
        try {
            const newStatus = await togglePageLock(pageId);
            toast.success(`${pageId} ${newStatus ? t('locked') : t('unlocked')}`);
        } catch (error) {
            toast.error(t('lockError'));
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30">
                    <Construction size={24} className="text-red-400" />
                </div>
                <div>
                    <h2 className="text-xl font-bold font-['Rajdhani'] text-white dark:text-white light:text-gray-900">
                        {t('lockMode')}
                    </h2>
                    <p className="text-sm text-gray-400 dark:text-gray-400 light:text-gray-600 mt-1">
                        {t('lockModeDescription')}
                    </p>
                </div>
            </div>

            {/* Lock Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {LOCKABLE_PAGES.map((page) => {
                    const Icon = page.icon;
                    const isLocked = isPageLockedForAdmin(page.id);
                    return (
                        <button
                            key={page.id}
                            onClick={() => handleToggle(page.id)}
                            data-testid={`lock-toggle-${page.id}`}
                            className={cn(
                                "flex items-center gap-4 p-4 rounded-xl border transition-all duration-200",
                                "hover:scale-[1.02] active:scale-[0.98]",
                                isLocked
                                    ? "bg-red-950/40 border-red-500/30 hover:border-red-500/50"
                                    : "bg-white/5 border-white/10 hover:border-white/20 dark:bg-white/5 light:bg-white light:border-gray-200 light:hover:border-gray-300"
                            )}
                        >
                            <div className={cn(
                                "p-2.5 rounded-lg",
                                isLocked ? "bg-red-500/20" : "bg-white/10 dark:bg-white/10 light:bg-gray-100"
                            )}>
                                <Icon size={20} className={isLocked ? "text-red-400" : "text-gray-400"} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className={cn(
                                    "font-semibold font-['Rajdhani']",
                                    isLocked ? "text-red-400" : "text-white dark:text-white light:text-gray-900"
                                )}>
                                    {page.label}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {isLocked ? t('locked') : t('unlocked')}
                                </p>
                            </div>
                            <div className={cn(
                                "p-2 rounded-lg",
                                isLocked ? "bg-red-500/20" : "bg-green-500/10"
                            )}>
                                {isLocked 
                                    ? <Lock size={16} className="text-red-400" /> 
                                    : <Unlock size={16} className="text-green-400" />
                                }
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
